import { Controller } from "@hotwired/stimulus"

/**
 * Take-off workspace UX for construction drawings.
 *
 * - Tracks markup-plan checkbox selection (reference sheets never appear here)
 * - Updates selection KPIs / summary copy
 * - Gates the run button when nothing is selected or take-off is locked
 * - Re-syncs after Turbo Stream replacements of the take-off section
 */
export default class extends Controller {
  static values = {
    drawingId: Number
  }

  static targets = [
    "planCheckbox",
    "selectedCount",
    "selectionSummary",
    "runButton",
    "form"
  ]

  connect() {
    this.boundProgress = (event) => this.applyProgress(event.detail)
    this.element.addEventListener("construction-drawing-ingest-status:progress", this.boundProgress)
    this.boundTurboRender = () => {
      this.recount()
      this.syncRunButton()
    }
    this.boundBeforeStream = (event) => {
      const original = event.detail.render
      event.detail.render = async (streamElement) => {
        await original(streamElement)
        requestAnimationFrame(() => {
          this.recount()
          this.syncRunButton()
        })
      }
    }

    document.addEventListener("turbo:render", this.boundTurboRender)
    document.addEventListener("turbo:frame-render", this.boundTurboRender)
    document.addEventListener("turbo:before-stream-render", this.boundBeforeStream)

    this.recount()
    this.syncRunButton()
  }

  disconnect() {
    this.element.removeEventListener("construction-drawing-ingest-status:progress", this.boundProgress)
    document.removeEventListener("turbo:render", this.boundTurboRender)
    document.removeEventListener("turbo:frame-render", this.boundTurboRender)
    document.removeEventListener("turbo:before-stream-render", this.boundBeforeStream)
  }

  // Stimulus target lifecycle — re-count when Turbo injects new checkboxes/buttons
  planCheckboxTargetConnected() {
    this.recount()
  }

  planCheckboxTargetDisconnected() {
    this.recount()
  }

  runButtonTargetConnected() {
    this.syncRunButton()
  }

  selectionSummaryTargetConnected() {
    this.recount()
  }

  // —— Actions ——

  recount() {
    const boxes = this.planCheckboxes()
    const checked = boxes.filter((el) => el.checked).length
    const total = boxes.length

    this.selectedCountTargets.forEach((el) => {
      // During a job run the server streams selected_page_count; don't fight it.
      if (el.dataset.takeoffServerLocked === "true") return
      el.textContent = String(checked)
    })

    this.selectionSummaryTargets.forEach((el) => {
      if (total === 0) {
        el.textContent = "No markup plans available"
        return
      }
      const noun = checked === 1 ? "plan" : "plans"
      el.textContent = `${checked} of ${total} markup ${noun} selected · native HexaPDF paths`
    })

    this.syncRunButton(checked)
  }

  ensureSelection(event) {
    const checked = this.planCheckboxes().filter((el) => el.checked).length
    if (checked > 0) return

    event.preventDefault()
    event.stopPropagation()
    this.flashSelectionWarning()
  }

  // Cockpit primary CTA — POST merge_markup via the real take-off form.
  submitTakeoff(event) {
    event.preventDefault()
    const btn = event.currentTarget
    if (btn?.disabled || btn?.dataset?.takeoffLocked === "true") return

    const formId = btn.dataset.takeoffFormId
    const form =
      (formId && document.getElementById(formId)) ||
      this.element.querySelector("form.cd-takeoff-form") ||
      this.element.querySelector("form[id^='takeoff_form_']")

    if (!form) {
      window.alert("Take-off form not ready. Open the Take-off stage and try again.")
      return
    }

    try {
      const shell = this.application.getControllerForElementAndIdentifier(this.element, "cd-shell")
      shell?.show?.("takeoff")
    } catch (_e) {
      /* ignore */
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  // —— Internals ——

  applyProgress(data) {
    if (!data || !("takeoff_locked" in data)) return
    const locked = data.takeoff_locked === true || data.takeoff_locked === "true"
    const running = data.takeoff_running === true || data.takeoff_running === "true"
    this.planCheckboxes().forEach((el) => { el.disabled = locked })
    this.element.querySelectorAll(".cd-takeoff-form [data-shift-checkbox-target='master']").forEach((el) => {
      el.disabled = locked
    })
    this.selectedCountTargets.forEach((el) => { el.dataset.takeoffServerLocked = String(running) })
    this.runButtonTargets.forEach((btn) => {
      btn.dataset.takeoffLocked = String(locked)
      if (data.takeoff_button_label) {
        if (btn instanceof HTMLInputElement) btn.value = data.takeoff_button_label
        else btn.textContent = data.takeoff_button_label
      }
    })
    this.recount()
  }

  planCheckboxes() {
    if (this.hasPlanCheckboxTarget) {
      return this.planCheckboxTargets
    }
    return Array.from(this.element.querySelectorAll(".takeoff-page-checkbox")).filter(
      (el) => el instanceof HTMLInputElement
    )
  }

  syncRunButton(checkedCount = null) {
    const checked =
      checkedCount == null
        ? this.planCheckboxes().filter((el) => el.checked).length
        : checkedCount

    this.runButtonTargets.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement || btn instanceof HTMLInputElement)) return

      const serverLocked = btn.dataset.takeoffLocked === "true"
      if (serverLocked) {
        btn.disabled = true
        return
      }

      btn.disabled = checked === 0
    })
  }

  flashSelectionWarning() {
    const summary = this.selectionSummaryTargets[0]
    if (!summary) {
      window.alert("Select at least one markup plan before take-off of native HexaPDF paths.")
      return
    }

    summary.textContent = "Select at least one markup plan"
    summary.classList.add("cd-selection-warn")
    summary.setAttribute("role", "status")

    const list = this.element.querySelector("#takeoff_markup_list_" + this.drawingIdValue)
    if (list) {
      list.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    window.clearTimeout(this._warnTimer)
    this._warnTimer = window.setTimeout(() => {
      summary.classList.remove("cd-selection-warn")
      this.recount()
    }, 2000)
  }
}
